import React from 'react';
import {useSelector} from 'react-redux';
import {MINUS_SIGN} from '../../../constants/text';
import {LEAGUE_CONFIGS} from '../../../configs/leagues';
import {RATING_K_BASE, RATING_LEAGUE_BONUS, RATING_LEAGUE_GAP_DECAY} from '../../../configs/ratingAlgorithm';
import {LEAGUE_DAILY_CRYSTALS, RATING_DAILY_PENALTY} from '../../../configs/ratingCrystals';
import {
  getVisibleRankedBotLeagueRow,
  VISIBLE_RANKED_BOT_LEAGUE_DISTRIBUTION,
  VISIBLE_RANKED_BOT_RATING_OFFSET,
} from '../../../configs/visibleRankedBots';
import {getVisibleBotIconPath} from '../../../utils/bots/getVisibleBotIconPath';
import {LEAGUE_ADJECTIVES} from '../../../utils/bots/getVisibleBotTitle';
import {pluralizeRu} from '../../../utils/other/pluralizeRu';
import BotLeagueBonuses from '../BotsRules/BotLeagueBonuses';
import './RankedRules.css';

// Насколько лига соперника отличается от вашей в таблице рейтинга за пару:
// от «на 3 лиги слабее» до «на 3 лиги сильнее». Дальше разница снова делится
// пополам на каждую лигу — это сказано под таблицей
const PAIR_TABLE_MAX_GAP = 3;

const formatSignedNumber = (value) => {
  const abs = Math.abs(value);
  const text = Number.isInteger(abs) ? `${abs}` : abs.toFixed(1).replace('.', ',');
  if (abs === 0) return '0';
  return `${value < 0 ? MINUS_SIGN : '+'}${text}`;
};

/**
 * Сколько рейтинга дает один соперник. gap — на сколько лиг он сильнее вас
 * (отрицательный — слабее). Та же формула, что в calculateRatingChanges на
 * сервере (ожидаемый результат по разнице силы лиг), и те же коэффициенты.
 */
export const getPairRatingDelta = (gap, isAhead) => {
  const expected = gap <= 0
    ? 1 - 0.5 * RATING_LEAGUE_GAP_DECAY ** -gap
    : 0.5 * RATING_LEAGUE_GAP_DECAY ** gap;
  return RATING_K_BASE * ((isAhead ? 1 : 0) - expected);
};

const getGapLabel = (gap) => {
  if (gap === 0) return 'Своей лиги';
  const count = Math.abs(gap);
  return `На ${count} ${pluralizeRu(count, 'лигу', 'лиги', 'лиг')} ${gap < 0 ? 'слабее' : 'сильнее'}`;
};

const PAIR_GAPS = Array.from({length: PAIR_TABLE_MAX_GAP * 2 + 1}, (_, index) => index - PAIR_TABLE_MAX_GAP);

// Подпись диапазона строки таблицы лиг ботов: до следующего порога или «и выше»
const getDistributionRangeLabel = (index) => {
  const row = VISIBLE_RANKED_BOT_LEAGUE_DISTRIBUTION[index];
  const nextRow = VISIBLE_RANKED_BOT_LEAGUE_DISTRIBUTION[index + 1];
  return nextRow ? `${row.minRating} – ${nextRow.minRating - 1}` : `${row.minRating} и выше`;
};

const DAILY_PENALTY_LEAGUE = LEAGUE_CONFIGS.find((league) => LEAGUE_DAILY_CRYSTALS[league.id]);

/**
 * Раздел правил «Рейтинг»: как меняется рейтинг, ежедневное списание, боты и
 * какие из них встречаются игроку. Все числа — из конфигов, по которым считает
 * сервер. Речь только о помеченных (в коде — «явных») ботах, но для игрока они
 * просто «боты»: слово «явный» и шансы подмены навели бы на мысль, что бывают
 * и другие.
 */
const RankedRules = () => {
  // Строка таблицы ботов для рейтинга игрока подсвечена: «что встретится мне»
  const rating = useSelector((state) => state.private?.auth?.rating ?? 0);
  const playerDistributionRow = getVisibleRankedBotLeagueRow(rating);
  const sameLeagueWinnerDelta = getPairRatingDelta(0, true) * 3;

  return (
    <div className="rules-page_content">
      <h2 className="rules-page_title">
        <img className={'rules-page_title-icon'} src={'/img/icons/star_rating.png'} alt={''} aria-hidden={'true'}/>
        Рейтинг
      </h2>

      <div className="rules-page_section">
        <p>
          Рейтинговый матч собирается автоматически: вы встаете в поиск, и игра подбирает соперников. По итогам матча
          меняется ваш рейтинг. Рейтинг определяет лигу, а от лиги зависят награды и то, ботов какой силы вы можете
          встретить.
        </p>
      </div>

      <div className="rules-page_section">
        <h2>Лиги</h2>
        <ul className="ranked-rules_leagues">
          {[...LEAGUE_CONFIGS].reverse().map((league) => (
            <li key={league.id}>
              <img className="ranked-rules_league-icon" src={league.iconPath} alt={''} aria-hidden={'true'}/>
              <b>{league.name}</b> — рейтинг от {league.minRating}
            </li>
          ))}
        </ul>
      </div>

      <div className="rules-page_section">
        <h2>Как меняется рейтинг</h2>
        <p>
          После матча ваше место сравнивается с местом каждого соперника по отдельности. Обошли соперника — получаете
          рейтинг, уступили ему — теряете, разделили с ним место — ничего не меняется. Изменение за матч — сумма по
          всем соперникам.
        </p>
        <p>
          Сколько дает каждый соперник, зависит только от разницы лиг, а не от точного рейтинга внутри лиги. Победа
          над соперником своей лиги дает {formatSignedNumber(getPairRatingDelta(0, true))}, поражение —{' '}
          {formatSignedNumber(getPairRatingDelta(0, false))}. Каждая лига разницы вдвое уменьшает то, что вы
          получаете за ожидаемый результат, и приближает к {RATING_K_BASE} то, что вы получаете или теряете за
          неожиданный:
        </p>
        <table className="ranked-rules_table">
          <thead>
            <tr>
              <th>Соперник</th>
              <th>Вы выше по месту</th>
              <th>Вы ниже по месту</th>
            </tr>
          </thead>
          <tbody>
            {PAIR_GAPS.map((gap) => (
              <tr key={gap} className={gap === 0 ? 'ranked-rules_table-row_accent' : ''}>
                <td>{getGapLabel(gap)}</td>
                <td>{formatSignedNumber(getPairRatingDelta(gap, true))}</td>
                <td>{formatSignedNumber(getPairRatingDelta(gap, false))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          Итог за матч округляется до целого. Например, в матче четырех игроков одной лиги победитель получает{' '}
          {formatSignedNumber(sameLeagueWinnerDelta)}, а последний теряет столько же.
        </p>
        <p>
          Место определяют очки победы, а при равных очках — дополнительные показатели итогов матча. Игроки, равные по
          всем показателям, делят место.
        </p>
        <h3>Надбавка лиги</h3>
        <p>
          Сверх этого за каждый рейтинговый матч начисляется надбавка по вашей лиге до матча — чем ниже лига, тем
          больше:
        </p>
        <ul>
          {LEAGUE_CONFIGS.map((league) => (
            <li key={league.id}>
              {league.name}: {RATING_LEAGUE_BONUS[league.id] > 0
                ? formatSignedNumber(RATING_LEAGUE_BONUS[league.id])
                : 'без надбавки'}
            </li>
          ))}
        </ul>
        <p>
          Надбавки нет, если все игроки матча закончили его вровень по всем показателям. Рейтинг не опускается
          ниже 0.
        </p>
      </div>

      {DAILY_PENALTY_LEAGUE && (
        <div className="rules-page_section">
          <h2>Ежедневное списание</h2>
          <p>
            В конце каждых игровых суток у игроков с рейтингом от {DAILY_PENALTY_LEAGUE.minRating} (
            {DAILY_PENALTY_LEAGUE.name} и выше) списывается {RATING_DAILY_PENALTY} рейтинга. В этот же момент они
            получают кристаллы за свою лигу. Удержаться в лиге можно только победами.
          </p>
        </div>
      )}

      <div className="rules-page_section">
        <h2>Боты</h2>
        <p>
          Если живых соперников для матча не хватает, в него может попасть бот. Бот помечен для всех игроков
          значком рядом с именем, а цвет значка показывает его лигу.
        </p>
        <ul>
          <li>Бот не получает ни наград, ни рейтинга.</li>
          <li>
            Место в итогах он занимает наравне со всеми: обойти его — то же, что обойти игрока его лиги, и для
            рейтинга, и для наград.
          </li>
          <li>
            В среднем рейтинге матча, от которого зависят награды, бот идет рейтингом на{' '}
            {VISIBLE_RANKED_BOT_RATING_OFFSET} выше нижней границы своей лиги, но не выходя за ее пределы.
          </li>
          <li>Чем выше лига бота, тем больше у него бонусов — см. ниже.</li>
        </ul>

        <h3>Какие боты вам встретятся</h3>
        <p>
          Лига бота зависит от рейтинга самого слабого живого игрока матча: соперник растет вместе с игроком.
          Внутри одного диапазона лига каждого бота выбирается случайно в таких долях:
        </p>
        <table className="ranked-rules_table ranked-rules_bots-table">
          <thead>
            <tr>
              <th>Рейтинг</th>
              <th>Лиги ботов</th>
            </tr>
          </thead>
          <tbody>
            {VISIBLE_RANKED_BOT_LEAGUE_DISTRIBUTION.map((row, index) => {
              const isPlayerRow = row === playerDistributionRow;
              return (
                <tr key={row.minRating} className={isPlayerRow ? 'ranked-rules_table-row_accent' : ''}>
                  <td>
                    {getDistributionRangeLabel(index)}
                    {isPlayerRow && <span className="ranked-rules_your-rating">ваш рейтинг</span>}
                  </td>
                  <td>
                    <span className="ranked-rules_bot-leagues">
                      {row.leagues.map(([leagueId, chance]) => (
                        <span key={leagueId} className="ranked-rules_bot-league" title={`${LEAGUE_ADJECTIVES[leagueId]} бот`}>
                          <img
                            className="ranked-rules_bot-icon"
                            src={getVisibleBotIconPath(leagueId, true)}
                            alt={`${LEAGUE_ADJECTIVES[leagueId]} бот`}
                          />
                          {Math.round(chance * 100)}%
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rules-page_section">
        <h2>Бонусы ботов по лигам</h2>
        <p>
          Бонусы те же, что в разделе «Боты»: экономика (прибавка к доходу, начиная с золотой лиги — еще и за каждый
          город бота), начиная с алмазной лиги — рост отрядов, у божественного бота — очки победы к доходу за каждый
          город и лучшая кость урона (кроме боя с другими ботами: против них он бросает обычные кости).
        </p>
        <BotLeagueBonuses/>
      </div>
    </div>
  );
};

export default RankedRules;
